import { supabase } from './supabaseClient'

import { checkIsMockMode, getMockData, saveMockData } from './mockHelper'
export { checkIsMockMode, getMockData, saveMockData }

export const dataService = {
  // Helper to open DB safely with upgradeneeded
  openDB(): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
      try {
        const req = indexedDB.open('TesScoreExamDB', 2)
        req.onupgradeneeded = (e) => {
          const db = (e.target as IDBOpenDBRequest).result
          if (!db.objectStoreNames.contains('exam_blobs')) {
            db.createObjectStore('exam_blobs', { keyPath: 'id' })
          }
          if (!db.objectStoreNames.contains('exam_meta')) {
            db.createObjectStore('exam_meta', { keyPath: 'id' })
          }
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      } catch (err) {
        reject(err)
      }
    })
  },

  // GET EXAM FILES
  async getExamFilesCount(): Promise<number> {
    return new Promise<number>(async (resolve) => {
      try {
        const db = await this.openDB()
        if (!db.objectStoreNames.contains('exam_meta')) {
          resolve(0)
          return
        }
        const tx = db.transaction('exam_meta', 'readonly')
        const countReq = tx.objectStore('exam_meta').count()
        countReq.onsuccess = () => resolve(countReq.result)
        countReq.onerror = () => resolve(0)
      } catch {
        resolve(0)
      }
    })
  },

  async getExamFilesList(): Promise<any[]> {
    return new Promise<any[]>(async (resolve) => {
      try {
        const db = await this.openDB()
        if (!db.objectStoreNames.contains('exam_meta')) {
          resolve([])
          return
        }
        const tx = db.transaction('exam_meta', 'readonly')
        const getReq = tx.objectStore('exam_meta').getAll()
        getReq.onsuccess = () => resolve(getReq.result || [])
        getReq.onerror = () => resolve([])
      } catch {
        resolve([])
      }
    })
  },

  // GET LISTS
  async getDepartments() {
    if (checkIsMockMode()) {
      return getMockData().departments
    }
    const { data } = await supabase.from('departments').select('*').order('name')
    return data || []
  },

  async getClassrooms() {
    if (checkIsMockMode()) {
      return getMockData().classrooms
    }
    const { data } = await supabase.from('classrooms').select('*').order('grade_level').order('room_number')
    return data || []
  },

  async updateClassroomRoomNumber(classroomId: number, roomNumber: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const idx = data.classrooms.findIndex((c: any) => c.id === classroomId)
      if (idx !== -1) {
        data.classrooms[idx].room_number = roomNumber
        saveMockData(data)
      }
      return true
    }
    const { error } = await supabase.from('classrooms').update({ room_number: roomNumber }).eq('id', classroomId)
    if (error) throw new Error(error.message)
    return true
  },

  async createClassroom(gradeLevel: number, roomNumber: string) {
    const newId = Math.floor(Math.random() * 1000000)
    if (checkIsMockMode()) {
      const data = getMockData()
      const newClass = { id: newId, grade_level: gradeLevel, room_number: roomNumber }
      data.classrooms.push(newClass)
      saveMockData(data)
      return newClass
    }
    const { data: newClasses, error } = await supabase.from('classrooms').insert({
      grade_level: gradeLevel,
      room_number: roomNumber
    }).select().single()
    if (error) throw new Error(error.message)
    return newClasses
  },

  async getSubjects() {
    if (checkIsMockMode()) {
      return getMockData().subjects
    }
    const { data } = await supabase.from('subjects').select('*').order('code')
    return data || []
  },

  async getStudents(classroomId?: number) {
    if (checkIsMockMode()) {
      const allStudents = getMockData().students
      return classroomId ? allStudents.filter((s: any) => s.classroom_id === classroomId) : allStudents
    }
    
    if (classroomId) {
      // Query student enrollments for the classroom and current active academic year
      const years = await this.getAcademicYears()
      const activeYear = years.find((y: any) => y.is_active)
      const yearId = activeYear?.id || 1
      
      const { data, error } = await supabase
        .from('student_enrollments')
        .select(`
          id,
          student:students (
            id,
            student_id_card,
            title,
            first_name,
            last_name,
            room_type,
            status,
            number_in_class
          )
        `)
        .eq('classroom_id', classroomId)
        .eq('academic_year_id', yearId)
        .or('status.eq.active,status.is.null')
        
      if (error) throw new Error(error.message)
      
      return (data || [])
        .filter((row: any) => row.student !== null)
        .map((row: any) => ({
          id: row.student.id,
          student_id_card: row.student.student_id_card,
          title: row.student.title,
          first_name: row.student.first_name,
          last_name: row.student.last_name,
          classroom_id: classroomId,
          room_type: row.student.room_type,
          status: row.student.status,
          number_in_class: row.student.number_in_class,
          student_enrollment_id: row.id
        }))
    }

    let allData: any[] = []
    let page = 0
    const pageSize = 1000
    let hasMore = true
    
    while (hasMore) {
      let query = supabase.from('students').select('*').range(page * pageSize, (page + 1) * pageSize - 1)
      const { data, error } = await query.order('student_id_card')
      if (error) throw new Error(error.message)
      if (!data || data.length === 0) {
        hasMore = false
      } else {
        allData = [...allData, ...data]
        if (data.length < pageSize) {
          hasMore = false
        } else {
          page++
        }
      }
    }
    return allData
  },

  async getProfiles() {
    if (checkIsMockMode()) {
      return getMockData().profiles
    }
    const { data } = await supabase.from('profiles').select('*')
    return data || []
  },

  async getTeachingAssignments() {
    if (checkIsMockMode()) {
      return getMockData().teachingAssignments
    }
    const { data } = await supabase.from('teaching_assignments').select('*')
    return data || []
  },

  async getAssessmentSessions() {
    if (checkIsMockMode()) {
      return getMockData().assessmentSessions
    }
    const { data } = await supabase.from('assessment_sessions').select('*')
    return data || []
  },

  async getScoreSubmissions() {
    if (checkIsMockMode()) {
      return getMockData().scoreSubmissions
    }
    const { data } = await supabase.from('score_submissions').select('*')
    return data || []
  },

  async getScores(subjectId?: number, classroomId?: number, sessionId?: number) {
    const userRole = localStorage.getItem('tesscore_user_role')
    const userId = localStorage.getItem('tesscore_user_id')
    
    if (userRole === 'head_of_department') {
      const isMock = checkIsMockMode()
      const assignments = isMock 
        ? getMockData().teachingAssignments 
        : (await supabase.from('teaching_assignments').select('*')).data || []
      
      const isAssigned = assignments.some((a: any) => 
        a.teacher_id === userId &&
        (!subjectId || a.subject_id === subjectId) &&
        (!classroomId || a.classroom_id === classroomId)
      )

      const examFiles = isMock
        ? getMockData().examFiles || []
        : (await supabase.from('exam_files').select('*')).data || []
      
      const isCoTeacherShared = examFiles.some((f: any) =>
        (!subjectId || f.subject_id === subjectId) &&
        f.tags &&
        f.tags.includes(`co_teacher:${userId}`)
      )

      if (!isAssigned && !isCoTeacherShared) {
        throw new Error('403 Forbidden: หัวหน้ากลุ่มสาระไม่มีสิทธิ์เข้าถึงคะแนนรายบุคคล')
      }
    }

    if (checkIsMockMode()) {
      let scores = getMockData().scores
      if (subjectId) scores = scores.filter((s: any) => s.subject_id === subjectId)
      if (sessionId) scores = scores.filter((s: any) => s.assessment_session_id === sessionId)
      if (classroomId) {
        const studentIds = getMockData().students.filter((st: any) => st.classroom_id === classroomId).map((st: any) => st.id)
        scores = scores.filter((s: any) => studentIds.includes(s.student_id))
      }
      return scores
    }
    
    if (!subjectId || !classroomId) {
      const activeUserRole = localStorage.getItem('tesscore_user_role')
      const activeUserId = localStorage.getItem('tesscore_user_id')

      let sseIds: number[] = []

      // If user is a teacher, pre-filter by their own teaching assignment IDs
      if (activeUserRole === 'teacher' && activeUserId) {
        const { data: teacherAssigns } = await supabase
          .from('teaching_assignments')
          .select('id')
          .eq('teacher_id', activeUserId)
        
        const teacherAssignIds = (teacherAssigns || []).map((ta: any) => ta.id)
        
        if (teacherAssignIds.length > 0) {
          const { data: teacherSses } = await supabase
            .from('student_subject_enrollments')
            .select('id')
            .in('teaching_assignment_id', teacherAssignIds)
          
          sseIds = (teacherSses || []).map((row: any) => row.id)
        }
        
        if (sseIds.length === 0) return []
      }

      let allScores: any[] = []
      let start = 0
      const pageSize = 1000
      let hasMore = true

      while (hasMore) {
        let query = supabase
          .from('scores')
          .select(`
            pre_test_score,
            post_test_score,
            assessment_session_id,
            student_subject_enrollment:student_subject_enrollments (
              teaching_assignment:teaching_assignments (
                subject_id
              ),
              student_enrollment:student_enrollments (
                student_id
              )
            )
          `)
          .range(start, start + pageSize - 1)

        if (sessionId) {
          query = query.eq('assessment_session_id', sessionId)
        }
        if (sseIds.length > 0) {
          query = query.in('student_subject_enrollment_id', sseIds)
        }

        const { data, error } = await query
        if (error) throw new Error(error.message)

        if (!data || data.length === 0) {
          hasMore = false
        } else {
          allScores.push(...data)
          start += pageSize
          if (data.length < pageSize) {
            hasMore = false
          }
        }
      }

      return allScores.map((sc: any) => ({
        student_id: sc.student_subject_enrollment?.student_enrollment?.student_id,
        subject_id: sc.student_subject_enrollment?.teaching_assignment?.subject_id,
        assessment_session_id: sc.assessment_session_id,
        pre_test_score: sc.pre_test_score,
        post_test_score: sc.post_test_score
      }))
    }

    // Find teaching assignments for subject and classroom in active academic year
    const years = await this.getAcademicYears()
    const activeYear = years.find((y: any) => y.is_active)
    const yearId = activeYear?.id || 1

    const { data: assignments } = await supabase
      .from('teaching_assignments')
      .select('id')
      .eq('subject_id', subjectId)
      .eq('classroom_id', classroomId)
      .eq('academic_year_id', yearId)

    const assignmentIds = (assignments || []).map((a: any) => a.id)
    if (assignmentIds.length === 0) return []

    // Fetch student subject enrollments matching these teaching assignments
    const { data: sseData, error: sseError } = await supabase
      .from('student_subject_enrollments')
      .select(`
        id,
        student_enrollment:student_enrollments (
          student_id
        )
      `)
      .in('teaching_assignment_id', assignmentIds)

    if (sseError) throw new Error(sseError.message)
    const sseIds = (sseData || []).map((row: any) => row.id)
    if (sseIds.length === 0) return []

    // Fetch scores matching student_subject_enrollment_ids
    let scoreQuery = supabase
      .from('scores')
      .select('student_subject_enrollment_id, pre_test_score, post_test_score')
      .in('student_subject_enrollment_id', sseIds)

    if (sessionId) scoreQuery = scoreQuery.eq('assessment_session_id', sessionId)
    const { data: scoreData, error: scoreError } = await scoreQuery
    if (scoreError) throw new Error(scoreError.message)

    // Map back to the legacy shape for frontend compatibility
    return (scoreData || []).map((sc: any) => {
      const sse = (sseData || []).find((row: any) => row.id === sc.student_subject_enrollment_id)
      const studentId = Array.isArray(sse?.student_enrollment)
        ? sse.student_enrollment[0]?.student_id
        : (sse?.student_enrollment as any)?.student_id
      return {
        student_id: studentId,
        pre_test_score: sc.pre_test_score,
        post_test_score: sc.post_test_score
      }
    })
  },

  async getDepartmentSummary(deptId: number, sessionId: number): Promise<any[]> {
    const { dashboardService } = await import('./dashboardService')
    return dashboardService.getDepartmentSummary(deptId, sessionId)
  },

  async logAuditEvent(userId: string | null, action: string, details: any) {
    if (checkIsMockMode()) {
      const data = getMockData()
      if (!data.auditLogs) data.auditLogs = []
      data.auditLogs.push({
        id: data.auditLogs.length + 1,
        user_id: userId,
        action,
        details,
        created_at: new Date().toISOString()
      })
      saveMockData(data)
      return true
    }

    try {
      const { error } = await supabase.from('audit_logs').insert({
        user_id: userId,
        action,
        details,
        created_at: new Date().toISOString()
      })
      if (error) {
        console.warn('Failed to insert audit log (Table audit_logs might not exist yet):', error.message)
      }
    } catch (err) {
      console.warn('Failed to insert audit log gracefully:', err)
    }
    return true
  },

  // MUTATIONS (Only implemented for mock mode here to support immediate admin UI editing)
  async toggleSession(sessionId: number, isOpen: boolean, userId: string | null = null) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.assessmentSessions.findIndex((s: any) => s.id === sessionId)
      if (index !== -1) {
        data.assessmentSessions[index].is_open = isOpen
        saveMockData(data)
      }
      await this.logAuditEvent(userId, isOpen ? 'open_session' : 'close_session', { sessionId })
      return true
    }
    await supabase.from('assessment_sessions').update({ is_open: isOpen }).eq('id', sessionId)
    await this.logAuditEvent(userId, isOpen ? 'open_session' : 'close_session', { sessionId })
    return true
  },

  async updateSessionDates(sessionId: number, startDate: string, endDate: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.assessmentSessions.findIndex((s: any) => s.id === sessionId)
      if (index !== -1) {
        data.assessmentSessions[index].start_date = startDate
        data.assessmentSessions[index].end_date = endDate
        saveMockData(data)
      }
      return true
    }
    await supabase.from('assessment_sessions').update({ start_date: startDate, end_date: endDate }).eq('id', sessionId)
    return true
  },

  async updateSession(sessionId: number, name: string, startDate: string, endDate: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.assessmentSessions.findIndex((s: any) => s.id === sessionId)
      if (index !== -1) {
        data.assessmentSessions[index].name = name
        data.assessmentSessions[index].start_date = startDate
        data.assessmentSessions[index].end_date = endDate
        saveMockData(data)
      }
      return true
    }
    await supabase.from('assessment_sessions').update({ name, start_date: startDate, end_date: endDate }).eq('id', sessionId)
    return true
  },

  async deleteAssessmentSession(sessionId: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.assessmentSessions = data.assessmentSessions.filter((s: any) => s.id !== sessionId)
      saveMockData(data)
      return true
    }
    await supabase.from('assessment_sessions').delete().eq('id', sessionId)
    return true
  },

  async createAssessmentSession(name: string, startDate: string, endDate: string, academicYearId?: number) {
    let yearId = academicYearId
    if (!yearId) {
      if (checkIsMockMode()) {
        const activeYear = getMockData().academicYears.find((y: any) => y.is_active)
        yearId = activeYear ? activeYear.id : 1
      } else {
        const { data: activeYears } = await supabase.from('academic_years').select('id').eq('is_active', true).limit(1)
        if (activeYears && activeYears.length > 0) {
          yearId = Number(activeYears[0].id)
        } else {
          yearId = 1
        }
      }
    }

    if (checkIsMockMode()) {
      const data = getMockData()
      const newSession = {
        id: data.assessmentSessions.length > 0 ? Math.max(...data.assessmentSessions.map((s: any) => s.id)) + 1 : 1,
        academic_year_id: yearId,
        name,
        start_date: startDate,
        end_date: endDate,
        is_open: true
      }
      data.assessmentSessions.push(newSession)
      saveMockData(data)
      return newSession
    }

    const { data, error } = await supabase
      .from('assessment_sessions')
      .insert({
        academic_year_id: yearId,
        name,
        start_date: startDate,
        end_date: endDate,
        is_open: true
      })
      .select()
      .single()

    if (error) throw new Error(error.message)
    return data
  },

  async updateProfileRole(profileId: string, role: 'admin' | 'head_of_department' | 'teacher', departmentId?: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.profiles.findIndex((p: any) => p.id === profileId)
      if (index !== -1) {
        data.profiles[index].role = role
        data.profiles[index].department_id = departmentId || null
        saveMockData(data)
      }
      return true
    }
    await supabase.from('profiles').update({ role, department_id: departmentId || null }).eq('id', profileId)
    return true
  },

  async saveScores(scoresToSave: any[], updatedBy: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const sample = scoresToSave[0]
      if (!sample) return true

      const session = data.assessmentSessions.find((s: any) => s.id === sample.assessment_session_id)
      if (!session) {
        throw new Error("ไม่พบข้อมูลรอบการบันทึกคะแนนในระบบ")
      }
      if (!session.is_open) {
        throw new Error("ขณะนี้ยังไม่มีรอบการบันทึกคะแนนที่เปิดใช้งาน กรุณาติดต่อผู้ดูแลระบบ")
      }
      const now = new Date()
      const endDate = new Date(session.end_date)
      if (now > endDate) {
        throw new Error("พ้นกำหนดระยะเวลากรอกคะแนนของรอบการบันทึกคะแนนนี้แล้ว")
      }

      const sessionName = (session.name || '').toLowerCase()
      const isPreSession = sessionName.includes('ก่อนเรียน') || sessionName.includes('pre')
      const isPostSession = sessionName.includes('หลังเรียน') || sessionName.includes('post')

      const submission = data.scoreSubmissions.find((sub: any) => 
        sub.teaching_assignment_id === sample.teaching_assignment_id && 
        sub.assessment_session_id === sample.assessment_session_id
      )
      if (submission) {
        if (isPreSession && submission.pre_test_status === 'submitted') {
          throw new Error("คะแนนก่อนเรียน (Pre-Test) ได้รับการยืนยันส่งเรียบร้อยแล้วและถูกล็อกแก้ไข")
        }
        if (isPostSession && submission.post_test_status === 'submitted') {
          throw new Error("คะแนนหลังเรียน (Post-Test) ได้รับการยืนยันส่งเรียบร้อยแล้วและถูกล็อกแก้ไข")
        }
      }

      scoresToSave.forEach((s: any) => {
        const index = data.scores.findIndex((sc: any) => 
          sc.student_id === s.student_id && 
          sc.subject_id === s.subject_id && 
          sc.assessment_session_id === s.assessment_session_id
        )
        const match = index !== -1 ? data.scores[index] : null

        if (isPreSession) {
          const newPost = s.post_test_score !== undefined && s.post_test_score !== null ? Number(s.post_test_score) : null
          const oldPost = match && match.post_test_score !== undefined && match.post_test_score !== null ? Number(match.post_test_score) : null
          if (newPost !== oldPost) {
            throw new Error("ขณะนี้ระบบเปิดให้บันทึกเฉพาะคะแนนก่อนเรียน (Pre-Test) ยังไม่สามารถบันทึกคะแนนหลังเรียน (Post-Test) ได้ กรุณารอจนกว่าผู้ดูแลระบบจะเปิดรอบการสอบ Post-Test")
          }
        }

        if (isPostSession) {
          const newPre = s.pre_test_score !== undefined && s.pre_test_score !== null ? Number(s.pre_test_score) : null
          const oldPre = match && match.pre_test_score !== undefined && match.pre_test_score !== null ? Number(match.pre_test_score) : null
          if (newPre !== oldPre) {
            throw new Error("ขณะนี้ระบบเปิดให้บันทึกเฉพาะคะแนนหลังเรียน (Post-Test) ไม่สามารถแก้ไขหรือเพิ่มคะแนนก่อนเรียน (Pre-Test) ได้")
          }
        }

        if (index !== -1) {
          data.scores[index] = { ...data.scores[index], ...s, updated_by: updatedBy }
        } else {
          data.scores.push({
            id: data.scores.length + 1,
            ...s,
            updated_by: updatedBy
          })
        }
      })

      // Log Audit Event
      if (!data.auditLogs) data.auditLogs = []
      data.auditLogs.push({
        id: data.auditLogs.length + 1,
        user_id: updatedBy,
        action: 'edit_scores',
        details: { subject_id: sample.subject_id, classroom_id: sample.classroom_id, assessment_session_id: sample.assessment_session_id, count: scoresToSave.length },
        created_at: new Date().toISOString()
      })

      saveMockData(data)
      return true
    }

    const sample = scoresToSave[0]
    if (!sample) return true
    
    const academicYearId = sample.academic_year_id
    const subjectId = sample.subject_id
    const teachingAssignmentId = sample.teaching_assignment_id
    const classroomId = sample.classroom_id
    const studentIds = scoresToSave.map((s: any) => s.student_id)

    let targetAssignmentId = teachingAssignmentId
    let targetClassroomId = classroomId

    if (!targetAssignmentId || !targetClassroomId) {
      const { data: assignments } = await supabase
        .from('teaching_assignments')
        .select('id, classroom_id')
        .eq('subject_id', subjectId)
        .eq('academic_year_id', academicYearId)
      
      if (assignments && assignments.length > 0) {
        targetAssignmentId = targetAssignmentId || assignments[0].id
        targetClassroomId = targetClassroomId || assignments[0].classroom_id
      }
    }

    // --- BACKEND-STYLE SCORE SESSION VALIDATION ---
    const { data: dbSession, error: sessionErr } = await supabase
      .from('assessment_sessions')
      .select('*')
      .eq('id', sample.assessment_session_id)
      .single()

    if (sessionErr || !dbSession) {
      throw new Error("ไม่พบข้อมูลรอบการบันทึกคะแนนในระบบ")
    }

    if (!dbSession.is_open) {
      throw new Error("ขณะนี้ยังไม่มีรอบการบันทึกคะแนนที่เปิดใช้งาน กรุณาติดต่อผู้ดูแลระบบ")
    }

    const now = new Date()
    const endDate = new Date(dbSession.end_date)
    if (now > endDate) {
      throw new Error("พ้นกำหนดระยะเวลากรอกคะแนนของรอบการบันทึกคะแนนนี้แล้ว")
    }

    const sessionName = (dbSession.name || '').toLowerCase()
    const isPreSession = sessionName.includes('ก่อนเรียน') || sessionName.includes('pre')
    const isPostSession = sessionName.includes('หลังเรียน') || sessionName.includes('post')

    // Check lock status from submissions
    if (targetAssignmentId) {
      const { data: submissionRow } = await supabase
        .from('score_submissions')
        .select('*')
        .eq('teaching_assignment_id', targetAssignmentId)
        .eq('assessment_session_id', sample.assessment_session_id)
        .maybeSingle()

      if (submissionRow) {
        if (isPreSession && submissionRow.pre_test_status === 'submitted') {
          throw new Error("คะแนนก่อนเรียน (Pre-Test) ได้รับการยืนยันส่งเรียบร้อยแล้วและถูกล็อกแก้ไข")
        }
        if (isPostSession && submissionRow.post_test_status === 'submitted') {
          throw new Error("คะแนนหลังเรียน (Post-Test) ได้รับการยืนยันส่งเรียบร้อยแล้วและถูกล็อกแก้ไข")
        }
      }
    }

    // 1. Fetch classroom enrollments for these students
    const { data: enrollments, error: enrollError } = await supabase
      .from('student_enrollments')
      .select('id, student_id, classroom_id')
      .eq('academic_year_id', academicYearId)
      .in('student_id', studentIds)
    
    if (enrollError) throw new Error(enrollError.message)
    const enrollmentsList = enrollments || []

    // 2. Insert missing student_enrollments on the fly
    const missingEnrollmentStudentIds = studentIds.filter(sid => 
      !enrollmentsList.some(e => Number(e.student_id) === Number(sid))
    )

    if (missingEnrollmentStudentIds.length > 0 && targetClassroomId) {
      const newEnrollments = missingEnrollmentStudentIds.map(sid => ({
        student_id: sid,
        classroom_id: targetClassroomId,
        academic_year_id: academicYearId,
        status: 'active'
      }))
      const { data: insertedEnrollments, error: insErr } = await supabase
        .from('student_enrollments')
        .insert(newEnrollments)
        .select('id, student_id, classroom_id')
      
      if (insErr) throw new Error(insErr.message)
      if (insertedEnrollments) enrollmentsList.push(...insertedEnrollments)
    }

    // 3. Fetch existing subject enrollments for these students
    const enrollmentIds = enrollmentsList.map((e: any) => e.id)
    let sseList: any[] = []
    if (enrollmentIds.length > 0 && targetAssignmentId) {
      const { data: existingSse, error: sseErr } = await supabase
        .from('student_subject_enrollments')
        .select('id, student_enrollment_id')
        .eq('teaching_assignment_id', targetAssignmentId)
        .in('student_enrollment_id', enrollmentIds)
      
      if (sseErr) throw new Error(sseErr.message)
      sseList = existingSse || []

      // 4. Insert missing student_subject_enrollments on the fly
      const missingSseEnrollments = enrollmentsList.filter(e => 
        !sseList.some(sse => Number(sse.student_enrollment_id) === Number(e.id))
      )

      if (missingSseEnrollments.length > 0) {
        const newSse = missingSseEnrollments.map(e => ({
          student_enrollment_id: e.id,
          teaching_assignment_id: targetAssignmentId
        }))
        const { data: insertedSse, error: insSseErr } = await supabase
          .from('student_subject_enrollments')
          .insert(newSse)
          .select('id, student_enrollment_id')
        
        if (insSseErr) throw new Error(insSseErr.message)
        if (insertedSse) sseList.push(...insertedSse)
      }
    }

    // 5. Map to scores table entries
    const dbScores = scoresToSave.map((s: any) => {
      const enrollmentRow = enrollmentsList.find((e: any) => Number(e.student_id) === Number(s.student_id))
      if (!enrollmentRow) {
        throw new Error(`ไม่พบการลงทะเบียนเรียนในห้องสำหรับนักเรียน ID: ${s.student_id}`)
      }
      const sseRow = sseList.find((sse: any) => Number(sse.student_enrollment_id) === Number(enrollmentRow.id))
      if (!sseRow) {
        throw new Error(`ไม่พบการลงทะเบียนเรียนในรายวิชาสำหรับนักเรียน ID: ${s.student_id}`)
      }

      return {
        student_subject_enrollment_id: sseRow.id,
        assessment_session_id: s.assessment_session_id,
        pre_test_score: s.pre_test_score,
        post_test_score: s.post_test_score,
        updated_by: updatedBy
      }
    })

    // Compare with existing DB values to enforce session-locked columns
    const targetSseIds = dbScores.map((ds: any) => ds.student_subject_enrollment_id)
    let existingDbScores: any[] = []
    if (targetSseIds.length > 0) {
      const { data: exScores } = await supabase
        .from('scores')
        .select('*')
        .eq('assessment_session_id', sample.assessment_session_id)
        .in('student_subject_enrollment_id', targetSseIds)
      existingDbScores = exScores || []
    }

    for (const newScore of dbScores) {
      const match = existingDbScores.find((es: any) => es.student_subject_enrollment_id === newScore.student_subject_enrollment_id)
      
      if (isPreSession) {
        const newPost = newScore.post_test_score !== undefined && newScore.post_test_score !== null ? Number(newScore.post_test_score) : null
        const oldPost = match && match.post_test_score !== undefined && match.post_test_score !== null ? Number(match.post_test_score) : null
        if (newPost !== oldPost) {
          throw new Error("ขณะนี้ระบบเปิดให้บันทึกเฉพาะคะแนนก่อนเรียน (Pre-Test) ยังไม่สามารถบันทึกคะแนนหลังเรียน (Post-Test) ได้ กรุณารอจนกว่าผู้ดูแลระบบจะเปิดรอบการสอบ Post-Test")
        }
      }

      if (isPostSession) {
        const newPre = newScore.pre_test_score !== undefined && newScore.pre_test_score !== null ? Number(newScore.pre_test_score) : null
        const oldPre = match && match.pre_test_score !== undefined && match.pre_test_score !== null ? Number(match.pre_test_score) : null
        if (newPre !== oldPre) {
          throw new Error("ขณะนี้ระบบเปิดให้บันทึกเฉพาะคะแนนหลังเรียน (Post-Test) ไม่สามารถแก้ไขหรือเพิ่มคะแนนก่อนเรียน (Pre-Test) ได้")
        }
      }
    }

    const { error } = await supabase.from('scores').upsert(dbScores, {
      onConflict: 'student_subject_enrollment_id,assessment_session_id'
    })
    if (error) throw new Error(error.message)

    // Log Audit Event
    await this.logAuditEvent(updatedBy, 'edit_scores', { 
      subject_id: subjectId, 
      classroom_id: classroomId, 
      assessment_session_id: sample.assessment_session_id, 
      count: scoresToSave.length 
    })

    return true
  },

  async submitScores(assignmentId: number, sessionId: number, type: 'pre' | 'post', userId: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.scoreSubmissions.findIndex((s: any) => 
        s.teaching_assignment_id === assignmentId && 
        s.assessment_session_id === sessionId
      )

      const statusField = type === 'pre' ? 'pre_test_status' : 'post_test_status'
      const timeField = type === 'pre' ? 'pre_test_submitted_at' : 'post_test_submitted_at'
      const userField = type === 'pre' ? 'pre_test_submitted_by' : 'post_test_submitted_by'

      if (index !== -1) {
        data.scoreSubmissions[index][statusField] = 'submitted'
        data.scoreSubmissions[index][timeField] = new Date().toISOString()
        data.scoreSubmissions[index][userField] = userId
      } else {
        data.scoreSubmissions.push({
          id: data.scoreSubmissions.length + 1,
          teaching_assignment_id: assignmentId,
          assessment_session_id: sessionId,
          pre_test_status: type === 'pre' ? 'submitted' : 'draft',
          post_test_status: type === 'post' ? 'submitted' : 'draft',
          pre_test_submitted_at: type === 'pre' ? new Date().toISOString() : null,
          post_test_submitted_at: type === 'post' ? new Date().toISOString() : null,
          pre_test_submitted_by: type === 'pre' ? userId : null,
          post_test_submitted_by: type === 'post' ? userId : null
        })
      }
      saveMockData(data)
      await this.logAuditEvent(userId, 'submit_scores', { assignmentId, sessionId, type })
      return true
    }

    // Check if entry exists first
    const { data: existing } = await supabase
      .from('score_submissions')
      .select('id')
      .eq('teaching_assignment_id', assignmentId)
      .eq('assessment_session_id', sessionId)
      .single()

    const updateObj: any = {}
    if (type === 'pre') {
      updateObj.pre_test_status = 'submitted'
      updateObj.pre_test_submitted_at = new Date().toISOString()
      updateObj.pre_test_submitted_by = userId
    } else {
      updateObj.post_test_status = 'submitted'
      updateObj.post_test_submitted_at = new Date().toISOString()
      updateObj.post_test_submitted_by = userId
    }

    if (existing) {
      await supabase.from('score_submissions').update(updateObj).eq('id', existing.id)
    } else {
      await supabase.from('score_submissions').insert({
        teaching_assignment_id: assignmentId,
        assessment_session_id: sessionId,
        ...updateObj
      })
    }
    await this.logAuditEvent(userId, 'submit_scores', { assignmentId, sessionId, type })
    return true
  },

  async registerTeacher(profile: any, subjectsList: any[], assignmentsList: any[], existingUserId?: string) {
    // 0. Resolve active academic year ID dynamically
    let activeYearId = 1
    if (checkIsMockMode()) {
      const activeYear = getMockData().academicYears.find((y: any) => y.is_active)
      activeYearId = activeYear ? activeYear.id : 1
    } else {
      const { data: activeYears } = await supabase.from('academic_years').select('id').eq('is_active', true).limit(1)
      if (activeYears && activeYears.length > 0) {
        activeYearId = Number(activeYears[0].id)
      }
    }

    if (checkIsMockMode()) {
      const data = getMockData()
      const profileId = existingUserId || 'teacher-' + Math.random().toString(36).substr(2, 9)
      
      // 1. Save profile
      data.profiles.push({
        id: profileId,
        email: profile.email,
        first_name: profile.first_name,
        last_name: profile.last_name,
        role: 'teacher',
        department_id: profile.department_id ? Number(profile.department_id) : 1,
        username: profile.username || profile.email,
        password: profile.password || 'password',
        status: 'pending_approval' // Newly registered accounts need admin approval
      })

      // 2. Save subjects
      subjectsList.forEach((sub: any) => {
        const exist = data.subjects.find((s: any) => s.code === sub.code)
        if (!exist) {
          data.subjects.push({
            id: sub.id,
            code: sub.code,
            name: sub.name,
            department_id: Number(profile.department_id) || 1,
            max_score_pre: Number(sub.max_score_pre),
            max_score_post: Number(sub.max_score_post)
          })
        } else {
          // Update score limits if subject already exists
          exist.max_score_pre = Number(sub.max_score_pre)
          exist.max_score_post = Number(sub.max_score_post)
        }
      })

      // 3. Save Assignments
      assignmentsList.forEach((assign: any) => {
        data.teachingAssignments.push({
          id: data.teachingAssignments.length + 1,
          teacher_id: profileId,
          subject_id: Number(assign.subject_id),
          classroom_id: Number(assign.classroom_id),
          academic_year_id: activeYearId
        })
      })

      saveMockData(data)
      return { success: true, profileId }
    }

    // In Supabase Production Mode:
    let profileId = existingUserId
    if (!profileId) {
      // Create Supabase Auth User
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: profile.email,
        password: profile.password,
        options: {
          data: {
            first_name: profile.first_name,
            last_name: profile.last_name
          }
        }
      })
      if (authError) throw new Error(authError.message)
      profileId = authData.user?.id
    }
    
    if (!profileId) throw new Error('การสร้างผู้ใช้งานล้มเหลว')

    // Save profile row
    const { error: profileErr } = await supabase.from('profiles').insert({
      id: profileId,
      email: profile.email,
      username: profile.username || profile.email,
      first_name: profile.first_name,
      last_name: profile.last_name,
      role: 'teacher',
      department_id: profile.department_id ? Number(profile.department_id) : 1,
      status: 'pending_approval'
    })
    if (profileErr) throw new Error(profileErr.message)

    // Save subjects and assignments
    for (const sub of subjectsList) {
      await supabase.from('subjects').upsert({
        id: sub.id,
        code: sub.code,
        name: sub.name,
        department_id: Number(profile.department_id) || 1,
        max_score_pre: Number(sub.max_score_pre),
        max_score_post: Number(sub.max_score_post)
      })
    }

    for (const assign of assignmentsList) {
      await supabase.from('teaching_assignments').insert({
        teacher_id: profileId,
        subject_id: Number(assign.subject_id),
        classroom_id: Number(assign.classroom_id),
        academic_year_id: activeYearId
      })
    }

    return { success: true, profileId }
  },

  async updateStudentStatus(studentId: number, status: 'active' | 'resigned' | 'transferred') {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.students.findIndex((st: any) => st.id === studentId)
      if (index !== -1) {
        data.students[index].status = status
        saveMockData(data)
      }
      return true
    }
    await supabase.from('students').update({ status }).eq('id', studentId)
    return true
  },

  async updateSubjectMaxScores(subjectId: number, maxPre: number, maxPost: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.subjects.findIndex((sub: any) => sub.id === subjectId)
      if (index !== -1) {
        data.subjects[index].max_score_pre = maxPre
        data.subjects[index].max_score_post = maxPost
        saveMockData(data)
      }
      return true
    }
    await supabase.from('subjects').update({ max_score_pre: maxPre, max_score_post: maxPost }).eq('id', subjectId)
    return true
  },

  async updateSubject(subjectId: number, updateObj: any) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.subjects.findIndex((sub: any) => sub.id === subjectId)
      if (index !== -1) {
        data.subjects[index] = { ...data.subjects[index], ...updateObj }
        saveMockData(data)
      }
      return true
    }
    const { error } = await supabase.from('subjects').update(updateObj).eq('id', subjectId)
    if (error) throw new Error(error.message)
    return true
  },

  async addAssignment(teacherId: string, subjectId: number, classroomId: number, academicYearId?: number) {
    let yearId = academicYearId
    if (!yearId) {
      if (checkIsMockMode()) {
        const activeYear = getMockData().academicYears.find((y: any) => y.is_active)
        yearId = activeYear ? activeYear.id : 1
      } else {
        const { data: activeYears } = await supabase.from('academic_years').select('id').eq('is_active', true).limit(1)
        if (activeYears && activeYears.length > 0) {
          yearId = Number(activeYears[0].id)
        } else {
          yearId = 1
        }
      }
    }
    if (checkIsMockMode()) {
      const data = getMockData()
      // Check duplicate
      const exist = data.teachingAssignments.some((a: any) => 
        a.teacher_id === teacherId && 
        a.subject_id === subjectId && 
        a.classroom_id === classroomId &&
        a.academic_year_id === yearId
      )
      if (!exist) {
        data.teachingAssignments.push({
          id: data.teachingAssignments.length + 1,
          teacher_id: teacherId,
          subject_id: subjectId,
          classroom_id: classroomId,
          academic_year_id: yearId
        })
        saveMockData(data)
      }
      return true
    }
    const { error } = await supabase.from('teaching_assignments').insert({
      teacher_id: teacherId,
      subject_id: subjectId,
      classroom_id: classroomId,
      academic_year_id: yearId
    })
    if (error) throw new Error(error.message)
    return true
  },

  async deleteAssignment(assignmentId: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.teachingAssignments = data.teachingAssignments.filter((a: any) => a.id !== assignmentId)
      saveMockData(data)
      return true
    }
    await supabase.from('teaching_assignments').delete().eq('id', assignmentId)
    return true
  },

  async updateAssignment(assignmentId: number, teacherId: string, subjectId: number, classroomId: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const idx = data.teachingAssignments.findIndex((a: any) => a.id === assignmentId)
      if (idx !== -1) {
        data.teachingAssignments[idx] = {
          ...data.teachingAssignments[idx],
          teacher_id: teacherId,
          subject_id: subjectId,
          classroom_id: classroomId
        }
        saveMockData(data)
      }
      return true
    }
    const { error } = await supabase
      .from('teaching_assignments')
      .update({ teacher_id: teacherId, subject_id: subjectId, classroom_id: classroomId })
      .eq('id', assignmentId)
    if (error) throw new Error(error.message)
    return true
  },


  async createSubject(code: string, name: string, departmentId: number, maxPre: number, maxPost: number) {
    const newId = Math.floor(Math.random() * 1000000)
    if (checkIsMockMode()) {
      const data = getMockData()
      const newSub = { id: newId, code, name, department_id: departmentId, max_score_pre: maxPre, max_score_post: maxPost }
      data.subjects.push(newSub)
      saveMockData(data)
      return newSub
    }
    const { data: subData } = await supabase.from('subjects').insert({
      code, name, department_id: departmentId, max_score_pre: maxPre, max_score_post: maxPost
    }).select().single()
    return subData
  },

  async updateTeacherProfile(profileId: string, firstName: string, lastName: string, departmentId: number, updateFields: any) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.profiles.findIndex((p: any) => p.id === profileId)
      if (index !== -1) {
        data.profiles[index] = {
          ...data.profiles[index],
          first_name: firstName,
          last_name: lastName,
          department_id: departmentId,
          ...updateFields
        }
        saveMockData(data)
      }
      return true
    }
    const updateObj: any = { 
      first_name: firstName, 
      last_name: lastName, 
      department_id: departmentId,
      ...updateFields
    }
    await supabase.from('profiles').update(updateObj).eq('id', profileId)
    return true
  },

  // GET LISTS FOR ACADEMIC DATES
  async getAcademicYears() {
    if (checkIsMockMode()) {
      return getMockData().academicYears
    }
    const { data } = await supabase.from('academic_years').select('*').order('year', { ascending: false }).order('semester', { ascending: false })
    return data || []
  },

  async updateAcademicYearDates(yearId: number, startDate: string, endDate: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.academicYears.findIndex((y: any) => y.id === yearId)
      if (index !== -1) {
        data.academicYears[index].start_date = startDate
        data.academicYears[index].end_date = endDate
        saveMockData(data)
      }
      return true
    }
    await supabase.from('academic_years').update({ start_date: startDate, end_date: endDate }).eq('id', yearId)
    return true
  },

  async createAcademicYear(year: number, semester: number, startDate: string, endDate: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      // Deactivate others
      data.academicYears.forEach((y: any) => { y.is_active = false })
      const newYear = {
        id: data.academicYears.length + 1,
        year,
        semester,
        is_active: true,
        start_date: startDate,
        end_date: endDate
      }
      data.academicYears.push(newYear)
      saveMockData(data)
      return newYear
    }
    // Set all others is_active to false first
    await supabase.from('academic_years').update({ is_active: false }).neq('id', 0)
    const { data: inserted } = await supabase.from('academic_years').insert({
      year, semester, is_active: true, start_date: startDate, end_date: endDate
    }).select().single()
    return inserted
  },

  // SIGNUP APPROVAL OPERATIONS
  async getPendingTeacherApprovals() {
    if (checkIsMockMode()) {
      const data = getMockData()
      return data.profiles.filter((p: any) => p.role === 'teacher' && p.status === 'pending_approval')
    }
    const { data } = await supabase.from('profiles').select('*').eq('role', 'teacher').eq('status', 'pending_approval')
    return data || []
  },

  async approveTeacherSignup(profileId: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.profiles.findIndex((p: any) => p.id === profileId)
      if (index !== -1) {
        data.profiles[index].status = 'approved'
        saveMockData(data)
      }
      return true
    }
    await supabase.from('profiles').update({ status: 'approved' }).eq('id', profileId)
    return true
  },

  async rejectTeacherSignup(profileId: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.profiles = data.profiles.filter((p: any) => p.id !== profileId)
      data.teachingAssignments = data.teachingAssignments.filter((a: any) => a.teacher_id !== profileId)
      saveMockData(data)
      return true
    }
    await supabase.from('profiles').delete().eq('id', profileId)
    return true
  },

  async deleteTeacherProfile(profileId: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.profiles = data.profiles.filter((p: any) => p.id !== profileId)
      data.teachingAssignments = data.teachingAssignments.filter((a: any) => a.teacher_id !== profileId)
      saveMockData(data)
      return true
    }
    const { error } = await supabase.from('profiles').delete().eq('id', profileId)
    if (error) throw new Error(error.message)
    return true
  },

  async transferTeacherAssignments(fromTeacherId: string, toTeacherId: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.teachingAssignments = data.teachingAssignments.map((a: any) => {
        if (a.teacher_id === fromTeacherId) {
          return { ...a, teacher_id: toTeacherId }
        }
        return a
      })
      saveMockData(data)
      return true
    }
    const { error } = await supabase
      .from('teaching_assignments')
      .update({ teacher_id: toTeacherId })
      .eq('teacher_id', fromTeacherId)
      
    if (error) throw new Error(error.message)
    return true
  },

  // UNLOCK SCORE REQUESTS OPERATIONS
  async getUnlockRequests() {
    if (checkIsMockMode()) {
      const data = getMockData()
      return data.unlockRequests || []
    }
    const { data } = await supabase.from('unlock_requests').select('*').order('created_at', { ascending: false })
    return (data || []).map((r: any) => ({
      ...r,
      teacher_id: r.requested_by
    }))
  },

  async createUnlockRequest(teachingAssignmentId: number, sessionId: number, testType: 'pre' | 'post', reason: string, teacherId: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      if (!data.unlockRequests) data.unlockRequests = []
      
      const newReq = {
        id: data.unlockRequests.length + 1,
        teaching_assignment_id: teachingAssignmentId,
        assessment_session_id: sessionId,
        test_type: testType,
        reason,
        teacher_id: teacherId,
        status: 'pending',
        created_at: new Date().toISOString()
      }
      data.unlockRequests.push(newReq)
      saveMockData(data)
      return newReq
    }
    const { data: inserted } = await supabase.from('unlock_requests').insert({
      teaching_assignment_id: teachingAssignmentId,
      assessment_session_id: sessionId,
      test_type: testType,
      reason,
      requested_by: teacherId,
      status: 'pending'
    }).select().single()
    return inserted ? { ...inserted, teacher_id: inserted.requested_by } : null
  },

  async approveUnlockRequest(requestId: number, approvedBy: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const reqIndex = data.unlockRequests.findIndex((r: any) => r.id === requestId)
      if (reqIndex !== -1) {
        const req = data.unlockRequests[reqIndex]
        req.status = 'approved'
        req.actioned_by = approvedBy
        req.actioned_at = new Date().toISOString()

        // RESET Score Submission back to draft!
        const subIndex = data.scoreSubmissions.findIndex((s: any) => 
          s.teaching_assignment_id === req.teaching_assignment_id && 
          s.assessment_session_id === req.assessment_session_id
        )
        if (subIndex !== -1) {
          if (req.test_type === 'pre') {
            data.scoreSubmissions[subIndex].pre_test_status = 'draft'
            data.scoreSubmissions[subIndex].pre_test_submitted_at = null
          } else {
            data.scoreSubmissions[subIndex].post_test_status = 'draft'
            data.scoreSubmissions[subIndex].post_test_submitted_at = null
          }
        }
        saveMockData(data)
        await this.logAuditEvent(approvedBy, 'approve_unlock', { requestId, test_type: req.test_type, teaching_assignment_id: req.teaching_assignment_id })
      }
      return true
    }

    // Supabase Mode: update request, then reset submission status in a transaction or sequential query
    const { data: req } = await supabase.from('unlock_requests').update({
      status: 'approved', resolved_by: approvedBy, resolved_at: new Date().toISOString()
    }).eq('id', requestId).select().single()

    if (req) {
      const statusField = req.test_type === 'pre' ? 'pre_test_status' : 'post_test_status'
      const timeField = req.test_type === 'pre' ? 'pre_test_submitted_at' : 'post_test_submitted_at'
      await supabase.from('score_submissions')
        .update({ [statusField]: 'draft', [timeField]: null })
        .eq('teaching_assignment_id', req.teaching_assignment_id)
        .eq('assessment_session_id', req.assessment_session_id)
      await this.logAuditEvent(approvedBy, 'approve_unlock', { requestId, test_type: req.test_type, teaching_assignment_id: req.teaching_assignment_id })
    }
    return true
  },

  async rejectUnlockRequest(requestId: number, rejectedBy: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const index = data.unlockRequests.findIndex((r: any) => r.id === requestId)
      if (index !== -1) {
        data.unlockRequests[index].status = 'rejected'
        data.unlockRequests[index].actioned_by = rejectedBy
        data.unlockRequests[index].actioned_at = new Date().toISOString()
        saveMockData(data)
        await this.logAuditEvent(rejectedBy, 'reject_unlock', { requestId })
      }
      return true
    }
    await supabase.from('unlock_requests').update({
      status: 'rejected', resolved_by: rejectedBy, resolved_at: new Date().toISOString()
    }).eq('id', requestId)
    await this.logAuditEvent(rejectedBy, 'reject_unlock', { requestId })
    return true
  },

  // BULK EXCEL IMPORTS
  async importStudentsExcel(classroomId: number, studentsList: any[]) {
    if (checkIsMockMode()) {
      const data = getMockData()
      let addedCount = 0
      
      studentsList.forEach((st: any) => {
        const exist = data.students.some((s: any) => s.student_id_card === String(st.student_id_card))
        if (!exist && st.first_name && st.last_name) {
          data.students.push({
            id: data.students.length + 1,
            student_id_card: String(st.student_id_card),
            first_name: st.first_name,
            last_name: st.last_name,
            classroom_id: classroomId,
            status: 'active'
          })
          addedCount++
        }
      })
      saveMockData(data)
      return addedCount
    }

    // Supabase Mode
    const formatted = studentsList.map((st: any) => ({
      student_id_card: String(st.student_id_card),
      first_name: st.first_name,
      last_name: st.last_name,
      classroom_id: classroomId,
      status: 'active'
    }))
    const { data: inserted, error } = await supabase.from('students').insert(formatted).select()
    if (error) throw new Error(error.message)
    return inserted ? inserted.length : 0
  },

  async importAssignmentsExcel(assignmentsList: any[]) {
    if (checkIsMockMode()) {
      const data = getMockData()
      let addedCount = 0

      for (const item of assignmentsList) {
        // Resolve subject
        let sub = data.subjects.find((s: any) => s.code === item.subject_code)
        if (!sub && item.subject_code && item.subject_name) {
          sub = {
            id: data.subjects.length + 1,
            code: item.subject_code,
            name: item.subject_name,
            department_id: Number(item.department_id) || 1,
            max_score_pre: 10,
            max_score_post: 10
          }
          data.subjects.push(sub)
        }

        // Resolve teacher
        const teacher = data.profiles.find((p: any) => p.email === item.teacher_email)

        // Resolve classroom
        const classroom = data.classrooms.find((c: any) => c.grade_level === Number(item.grade_level) && c.room_number === String(item.room_number))

        if (sub && teacher && classroom) {
          const exist = data.teachingAssignments.some((a: any) => 
            a.teacher_id === teacher.id && 
            a.subject_id === sub.id && 
            a.classroom_id === classroom.id
          )
          if (!exist) {
            data.teachingAssignments.push({
              id: data.teachingAssignments.length + 1,
              teacher_id: teacher.id,
              subject_id: sub.id,
              classroom_id: classroom.id,
              academic_year_id: 1
            })
            addedCount++
          }
        }
      }
      saveMockData(data)
      return addedCount
    }

    // In Supabase mode, the logic would query database IDs and run inserts.
    // Simulating resolving codes -> inserting. For Vite demo SPA, mock handles it fully.
    return assignmentsList.length
  },

  async addStudent(student: any) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const newSt = {
        id: data.students.length + 1,
        student_id_card: String(student.student_id_card),
        title: student.title || 'เด็กชาย',
        first_name: student.first_name,
        last_name: student.last_name,
        classroom_id: Number(student.classroom_id),
        number_in_class: Number(student.number_in_class || 1),
        room_type: student.room_type || 'Normal',
        status: 'active'
      }
      data.students.push(newSt)
      saveMockData(data)
      try {
        const { dashboardService } = await import('./dashboardService')
        await dashboardService.syncEnrollments()
      } catch (e) {
        console.error('Sync error in mock addStudent:', e)
      }
      return newSt
    }
    const { data: inserted, error } = await supabase.from('students').insert(student).select().single()
    if (error) throw new Error(error.message)
    try {
      const { dashboardService } = await import('./dashboardService')
      await dashboardService.syncEnrollments()
    } catch (e) {
      console.error('Sync error in addStudent:', e)
    }
    return inserted
  },

  async updateStudent(studentId: number, updateObj: any) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const idx = data.students.findIndex((st: any) => st.id === studentId)
      if (idx !== -1) {
        data.students[idx] = { ...data.students[idx], ...updateObj }
        saveMockData(data)
      }
      return true
    }
    const { error } = await supabase.from('students').update(updateObj).eq('id', studentId)
    if (error) throw new Error(error.message)
    return true
  },

  async bulkUpdateStudentsClassroom(studentIds: number[], classroomId: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      studentIds.forEach(id => {
        const idx = data.students.findIndex((st: any) => st.id === id)
        if (idx !== -1) {
          data.students[idx].classroom_id = classroomId
        }
      })
      saveMockData(data)
      return true
    }
    const { error } = await supabase.from('students').update({ classroom_id: classroomId }).in('id', studentIds)
    if (error) throw new Error(error.message)
    return true
  },

  async bulkUpdateStudentsRoomType(studentIds: number[], roomType: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      studentIds.forEach(id => {
        const idx = data.students.findIndex((st: any) => st.id === id)
        if (idx !== -1) {
          data.students[idx].room_type = roomType
        }
      })
      saveMockData(data)
      return true
    }
    const { error } = await supabase.from('students').update({ room_type: roomType }).in('id', studentIds)
    if (error) throw new Error(error.message)
    return true
  },

  async deleteStudent(studentId: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.students = data.students.filter((st: any) => st.id !== studentId)
      saveMockData(data)
      return true
    }
    const { error } = await supabase.from('students').delete().eq('id', studentId)
    if (error) throw new Error(error.message)
    return true
  },

  async getClassroomTypes() {
    if (checkIsMockMode()) {
      const data = getMockData()
      if (!data.classroomTypes) {
        data.classroomTypes = ['Normal', 'Gifted', 'EP', 'SME']
        saveMockData(data)
      }
      return data.classroomTypes
    }
    const { data, error } = await supabase.from('classroom_types').select('name').order('name')
    if (error) throw new Error(error.message)
    if (!data || data.length === 0) {
      return ['Normal', 'Gifted', 'EP', 'SME']
    }
    return data.map((d: any) => d.name)
  },

  async createClassroomType(name: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      if (!data.classroomTypes) data.classroomTypes = ['Normal', 'Gifted', 'EP', 'SME']
      if (!data.classroomTypes.includes(name)) {
        data.classroomTypes.push(name)
        saveMockData(data)
      }
      return true
    }
    const { error } = await supabase.from('classroom_types').insert({ name })
    if (error) throw new Error(error.message)
    return true
  },

  async deleteClassroomType(name: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      if (data.classroomTypes) {
        data.classroomTypes = data.classroomTypes.filter((t: any) => t !== name)
        saveMockData(data)
      }
      return true
    }
    const { error } = await supabase.from('classroom_types').delete().eq('name', name)
    if (error) throw new Error(error.message)
    return true
  },

  async deleteSubject(subjectId: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.subjects = data.subjects.filter((s: any) => s.id !== subjectId)
      data.teachingAssignments = data.teachingAssignments.filter((a: any) => a.subject_id !== subjectId)
      saveMockData(data)
      return true
    }
    const { error } = await supabase.from('subjects').delete().eq('id', subjectId)
    if (error) throw new Error(error.message)
    return true
  },

  async importSubjectsExcel(subjectsList: any[]) {
    if (checkIsMockMode()) {
      const data = getMockData()
      let count = 0
      subjectsList.forEach((s: any) => {
        const idx = data.subjects.findIndex((sub: any) => sub.code === s.code)
        const deptName = String(s.department_name || 'ทั่วไป').trim()
        let matchedDept = data.departments.find((d: any) => d.name.toLowerCase() === deptName.toLowerCase())
        if (!matchedDept) {
          matchedDept = { id: data.departments.length + 1, name: deptName }
          data.departments.push(matchedDept)
        }
        const newRecord = {
          id: idx !== -1 ? data.subjects[idx].id : data.subjects.length + 1,
          code: s.code,
          name: s.name,
          credits: Number(s.credits || 1.5),
          grade_level: Number(s.grade_level || 7),
          department_id: matchedDept.id,
          max_score_pre: 10,
          max_score_post: 10
        }
        if (idx !== -1) {
          data.subjects[idx] = newRecord
        } else {
          data.subjects.push(newRecord)
        }
        count++
      })
      saveMockData(data)
      return count
    }

    // 1. Fetch all existing departments
    const { data: allDepts, error: deptsErr } = await supabase.from('departments').select('*')
    if (deptsErr) throw new Error(deptsErr.message)

    // 2. Identify missing departments
    const missingDepts = Array.from(new Set(
      subjectsList
        .map(s => String(s.department_name || 'ทั่วไป').trim())
        .filter(name => name && !allDepts.some((d: any) => d.name.toLowerCase() === name.toLowerCase()))
    ))

    // 3. Insert missing departments
    if (missingDepts.length > 0) {
      const inserts = missingDepts.map(name => ({ name }))
      const { data: newDepts, error: insDeptsErr } = await supabase.from('departments').insert(inserts).select()
      if (insDeptsErr) throw new Error(insDeptsErr.message)
      if (newDepts) {
        allDepts.push(...newDepts)
      }
    }

    // 4. Map subjects to their department IDs and format
    const formatted = subjectsList.map((s: any) => {
      const deptName = String(s.department_name || 'ทั่วไป').trim()
      const deptObj = allDepts.find((d: any) => d.name.toLowerCase() === deptName.toLowerCase())
      
      const record: any = {
        code: s.code,
        name: s.name,
        credits: Number(s.credits || 1.5),
        grade_level: Number(s.grade_level || 7),
        department_id: deptObj ? deptObj.id : null,
        max_score_pre: 10,
        max_score_post: 10
      }
      
      if (record.department_id === null && allDepts.length > 0) {
        record.department_id = allDepts[0].id
      }
      
      if (s.id) {
        record.id = s.id
      }
      return record
    })
    const { data: inserted, error } = await supabase
      .from('subjects')
      .upsert(formatted, { onConflict: 'code' })
      .select()
    if (error) throw new Error(error.message)
    return inserted ? inserted.length : 0
  },

  async adminUpdateUserProfile(userId: string, updateObj: any) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const idx = data.profiles.findIndex((p: any) => p.id === userId)
      if (idx !== -1) {
        data.profiles[idx] = { ...data.profiles[idx], ...updateObj }
        saveMockData(data)
      }
      return true
    }
    const { error: firstError } = await supabase.from('profiles').update(updateObj).eq('id', userId)
    if (firstError) {
      if (firstError.message.includes('column "password"') || firstError.code === '42703') {
        const cleanUpdate = { ...updateObj }
        delete cleanUpdate.password
        const { error: secondError } = await supabase.from('profiles').update(cleanUpdate).eq('id', userId)
        if (secondError) throw new Error(secondError.message)
      } else {
        throw new Error(firstError.message)
      }
    }
    return true
  },

  async updateDepartment(id: number, name: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const idx = data.departments.findIndex((d: any) => d.id === id)
      if (idx !== -1) {
        data.departments[idx].name = name
        saveMockData(data)
      }
      return true
    }
    const { error } = await supabase.from('departments').update({ name }).eq('id', id)
    if (error) throw new Error(error.message)
    return true
  },

  async updateClassroomType(oldName: string, newName: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      if (data.classroomTypes) {
        const idx = data.classroomTypes.indexOf(oldName)
        if (idx !== -1) {
          data.classroomTypes[idx] = newName
          saveMockData(data)
        }
      }
      return true
    }
    const { error } = await supabase.from('classroom_types').update({ name: newName }).eq('name', oldName)
    if (error) throw new Error(error.message)
    return true
  },

  async createDepartment(name: string) {
    if (checkIsMockMode()) {
      const data = getMockData()
      if (!data.departments.some((d: any) => d.name === name)) {
        data.departments.push({
          id: data.departments.length + 1,
          name
        })
        saveMockData(data)
      }
      return true
    }
    const { error } = await supabase.from('departments').insert({ name })
    if (error) throw new Error(error.message)
    return true
  },

  async deleteDepartment(id: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.departments = data.departments.filter((d: any) => d.id !== id)
      saveMockData(data)
      return true
    }
    const { error } = await supabase.from('departments').delete().eq('id', id)
    if (error) throw new Error(error.message)
    return true
  },

  async importStudentsExcelComplete(studentsList: any[]) {
    // Helper to parse grade and room consistently
    const parseGradeAndRoom = (gradeAndRoomStr: string) => {
      let grade = 7
      let room = '1'
      if (gradeAndRoomStr) {
        let cleanStr = String(gradeAndRoomStr).replace(/\s+/g, '')
        if (cleanStr.startsWith('ม.')) {
          cleanStr = cleanStr.substring(2)
        } else if (cleanStr.startsWith('ม')) {
          cleanStr = cleanStr.substring(1)
        }
        const split = cleanStr.split('/')
        if (split.length >= 2) {
          const parsedGrade = parseInt(split[0], 10)
          if (!isNaN(parsedGrade)) {
            grade = parsedGrade
            if (grade >= 1 && grade <= 6) {
              grade = grade + 6
            }
          }
          room = split[1] // Extract only the room part, e.g. "1"
        } else {
          const parsedGrade = parseInt(cleanStr, 10)
          if (!isNaN(parsedGrade)) {
            grade = parsedGrade
            if (grade >= 1 && grade <= 6) {
              grade = grade + 6
            }
          }
          room = '1'
        }
      }
      return { grade, room }
    }

    if (checkIsMockMode()) {
      const data = getMockData()
      let count = 0
      studentsList.forEach((st: any) => {
        const { grade, room } = parseGradeAndRoom(st.grade_and_room)
        
        let classroomObj = data.classrooms.find((c: any) => c.grade_level === grade && c.room_number === room)
        if (!classroomObj) {
          classroomObj = { id: data.classrooms.length + 1, grade_level: grade, room_number: room }
          data.classrooms.push(classroomObj)
        }

        const exist = data.students.some((s: any) => s.student_id_card === String(st.student_id_card))
        if (!exist) {
          data.students.push({
            id: data.students.length + 1,
            student_id_card: String(st.student_id_card),
            title: st.title || 'เด็กชาย',
            first_name: st.first_name,
            last_name: st.last_name,
            classroom_id: classroomObj.id,
            number_in_class: Number(st.number_in_class || 1),
            room_type: st.room_type || 'Normal',
            status: 'active'
          })
          count++
        }
      })
      saveMockData(data)
      return count
    }

    const { data: allClassrooms, error: classErr } = await supabase.from('classrooms').select('*')
    if (classErr) throw new Error(classErr.message)

    const { data: allTypesData, error: typesErr } = await supabase.from('classroom_types').select('name')
    if (typesErr) throw new Error(typesErr.message)
    const allRoomTypes = (allTypesData || []).map((t: any) => t.name)

    // Pre-parse and normalize students
    const normalized = studentsList.map(st => {
      const { grade, room } = parseGradeAndRoom(st.grade_and_room)
      return { ...st, grade, room }
    })

    // Batch insert missing classroom types
    const newRoomTypes = Array.from(new Set(
      normalized
        .map(s => String(s.room_type || 'Normal').trim())
        .filter(t => t && !allRoomTypes.some(art => art.toLowerCase() === t.toLowerCase()))
    ))

    if (newRoomTypes.length > 0) {
      const typeInserts = newRoomTypes.map(t => ({ name: t }))
      const { error: insTypesErr } = await supabase.from('classroom_types').insert(typeInserts)
      if (insTypesErr) {
        console.warn('Failed to insert classroom types in bulk:', insTypesErr.message)
      } else {
        allRoomTypes.push(...newRoomTypes)
      }
    }

    // Batch insert missing classrooms
    const newClassroomsToInsert: any[] = []
    normalized.forEach(st => {
      const exists = allClassrooms.some((c: any) => c.grade_level === st.grade && c.room_number === st.room)
      const inInsertList = newClassroomsToInsert.some(c => c.grade_level === st.grade && c.room_number === st.room)
      if (!exists && !inInsertList) {
        newClassroomsToInsert.push({
          grade_level: st.grade,
          room_number: st.room
        })
      }
    })

    if (newClassroomsToInsert.length > 0) {
      const { data: newClasses, error: insClassErr } = await supabase.from('classrooms').insert(newClassroomsToInsert).select()
      if (insClassErr) throw new Error(insClassErr.message)
      if (newClasses) {
        allClassrooms.push(...newClasses)
      }
    }

    // Bulk upsert new/updated students
    const studentsToUpsert: any[] = []
    const processedIds = new Set<string>()

    normalized.forEach(st => {
      const idCard = String(st.student_id_card).trim()
      if (processedIds.has(idCard)) {
        return // skip duplicate ID cards inside this chunk
      }
      processedIds.add(idCard)

      // Must match BOTH grade_level AND room_number — room_number alone is not unique across grades
      const classroomObj = allClassrooms.find((c: any) => c.grade_level === st.grade && c.room_number === st.room)
      if (!classroomObj) return // safety check

      studentsToUpsert.push({
        student_id_card: idCard,
        title: st.title || 'เด็กชาย',
        first_name: st.first_name,
        last_name: st.last_name,
        classroom_id: classroomObj.id,
        number_in_class: Number(st.number_in_class || 1),
        room_type: st.room_type || 'Normal',
        status: 'active'
      })
    })

    if (studentsToUpsert.length > 0) {
      const { data: inserted, error: insStErr } = await supabase
        .from('students')
        .upsert(studentsToUpsert, { onConflict: 'student_id_card' })
        .select()
      if (insStErr) throw new Error(insStErr.message)
      try {
        const { dashboardService } = await import('./dashboardService')
        await dashboardService.syncEnrollments()
      } catch (e) {
        console.error('Sync error in importStudentsExcelComplete:', e)
      }
      return inserted ? inserted.length : 0
    }

    return 0
  },

  async clearAllData() {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.scores = []
      data.unlockRequests = []
      data.scoreSubmissions = []
      data.teachingAssignments = []
      data.students = []
      data.subjects = []
      data.classrooms = []
      saveMockData(data)
      return true
    }
    await supabase.from('scores').delete().gt('id', 0)
    await supabase.from('unlock_requests').delete().gt('id', 0)
    await supabase.from('score_submissions').delete().gt('id', 0)
    await supabase.from('teaching_assignments').delete().gt('id', 0)
    await supabase.from('students').delete().gt('id', 0)
    await supabase.from('subjects').delete().gt('id', 0)
    await supabase.from('classrooms').delete().gt('id', 0)
    return true
  },

  async clearAllSubjects() {
    if (checkIsMockMode()) {
      const data = getMockData()
      data.subjects = []
      data.scores = []
      data.teachingAssignments = []
      data.scoreSubmissions = []
      saveMockData(data)
      return true
    }
    await supabase.from('scores').delete().gt('id', 0)
    await supabase.from('teaching_assignments').delete().gt('id', 0)
    await supabase.from('score_submissions').delete().gt('id', 0)
    await supabase.from('subjects').delete().gt('id', 0)
    return true
  },

  async clearClassroomData(classroomId: number) {
    if (checkIsMockMode()) {
      const data = getMockData()
      const classStIds = data.students.filter((st: any) => st.classroom_id === classroomId).map((st: any) => st.id)
      data.students = data.students.filter((st: any) => st.classroom_id !== classroomId)
      data.scores = data.scores.filter((sc: any) => !classStIds.includes(sc.student_id))
      data.teachingAssignments = data.teachingAssignments.filter((a: any) => a.classroom_id !== classroomId)
      saveMockData(data)
      return true
    }
    const { data: sts } = await supabase.from('students').select('id').eq('classroom_id', classroomId)
    if (sts && sts.length > 0) {
      const stIds = sts.map(s => s.id)
      await supabase.from('scores').delete().in('student_id', stIds)
    }
    await supabase.from('teaching_assignments').delete().eq('classroom_id', classroomId)
    await supabase.from('students').delete().eq('classroom_id', classroomId)
    await supabase.from('classrooms').delete().eq('id', classroomId)
    return true
  }
}

